<template>
    <div id="Instructions" style="min-height: inherit; display: flex; flex-direction: column;">
        <Header title="Reporting Instructions" />
        <div class="mainbody">
            <div id="content">
                <h3>Reporting Period</h3>
                <div class="InstructionSection">
                    <p>Payroll reports can be submitted each month from the 20th of that month to the 10th of the
                        following month (or the next weekday). If payroll is not reported before the 7th, we will send
                        an email reminder.</p>

                    <div class="Example">
                        <div class="Title">Example</div>
                        Payroll paid in <em>December 2022</em> can be reported on this website from <em>December 20, 2022, to January 10, 2023</em>.
                        An email reminder would be sent on <em>January 7th</em> if payroll was not reported prior.
                    </div>

                    <p>If payroll is not reported during the reporting period:</p>
                    <ul class="InstructionList">
                        <li>You may <router-link to=ContactUs>call your account manager</router-link> by the 13th to provide a payroll report over the phone,</li>
                        <li>Or an email invoice will be sent on the 14th containing an estimated payment of one-twelfth (1/12) of the policy premium cost.</li>
                    </ul>

                    <p>The reported (or estimated) payment will be withdrawn on the 15th (or next business day).</p>
                </div>

                <h3>What should I report?</h3>
                <div class="InstructionSection">
                    <p>For each policy and class code, <strong>report all regular payroll paid during the reporting period</strong>.
                        The amount reported should be based on pay dates that took place during the reporting period
                        along with the following inclusions and exclusions, depending on state:</p>

                    <ul class="SectionDetailList">
                        <li><strong>Overtime</strong> &ndash; Exclude one-third (&frac13;) of overtime.
                            <div class="Exceptions">
                                <div class="Title">Exceptions</div>
                                <div class="Details">
                                    <strong>Pennsylvania</strong>: Do <em>not</em> exclude any overtime.
                                </div>
                            </div>
                            <div class="Example">
                                <div class="Title">Example</div>
                                <p>An employee works 40 hours of regular time at $10 per hour and 1 hour of overtime
                                    at $15 per hour. <em>Multiply the total overtime pay by 0.67 and round to the nearest dollar</em>:</p>
                                <div class="MathExample">
                                    <div>Regular time:</div><div class="Math">40 hr x $10 = $400</div>
                                    <div>Overtime:</div><div class="Math">1 hr x $15 = $15 x 0.67 = $10.05 <span class="Comment">(Round to $10.00)</span></div>
                                    <div>Total:</div><div class="Math">$400 + $10 = $410</div>
                                </div>
                            </div>
                        </li>
                        <li><strong>Double-Time</strong> &ndash; Exclude one-half (&frac12;) of Double-Time.
                            <div class="Exceptions">
                                <div class="Title">Exceptions</div>
                                <div class="Details">
                                    <strong>Pennsylvania</strong>: Do <em>not</em> exclude any Double-Time.
                                </div>
                            </div>
                            <div class="Example">
                                <div class="Title">Example</div>
                                <p>An employee works 40 hours of regular time at $10 per hour and 1 hour of double time
                                    at $20 per hour. <em>Multiply the total double time pay by 0.5 and round to the nearest dollar</em>:</p>
                                <div class="MathExample">
                                    <div>Regular time:</div><div class="Math">40 hr x $10 = $400</div>
                                    <div>Double time:</div><div class="Math">1 hr x $20 = $20 x 0.5 = $10</div>
                                    <div>Total:</div><div class="Math">$400 + $10 = $410</div>
                                </div>
                            </div>
                        </li>
                        <li><strong>Bonuses, Sick and Vacation Time</strong> &ndash; Include.</li>
                        <li><strong>Severance Pay and Tips</strong> &ndash; Exclude.</li>
                        <li><strong>Section 125 Deductions</strong> &ndash; Include.
                            <div class="Exceptions">
                                <div class="Title">Exceptions</div>
                                <div class="Details">
                                    <strong>California</strong>: Do <em>not</em> include any Section 125 deductions.
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>

                <h3>Renewal</h3>
                <div class="InstructionSection">
                    <p>When your policy renews and your effective date is not the first of the month, you will report gross
                        payroll for both the expiring policy and the new policy.  This only applies if both policies use
                        payroll reporting.</p>

                    <div class="Example">
                        <div class="Title">Example</div>
                        <p>A policy that renews in the middle of <em>August 2022</em> may appear like this:</p>
                        <img src="/img/TwoPolicy.png" alt="screen shot"/>
                        <p>Report the paychecks that occurred between 08/01/2022 and 08/24/2022 in the <em>upper</em> table and those
                            between 08/25/2022 and 08/31/2022 in the <em>lower</em> table.  Make sure to report "0" if you have no wages
                            to report for either period so an estimate is not calculated.</p>
                    </div>
                </div>
            </div>
            <CompanyInfo />
        </div>
    </div>
</template>

<script>
    import Header from "./shared/Header";
    import CompanyInfo from "./shared/CompanyInfo";

    export default {
        name: "Instructions",
        components: { Header, CompanyInfo },

    }
</script>

<style scoped>
</style>